import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import styled, { css } from "styled-components";

import LandingHeroImage from "assets/png/landingPage/LandingHeroImage.png";

import HtmlHead from "components/atoms/HtmlHead";
import Image from "components/atoms/Image";
import Text from "components/atoms/text/Text";

import FlashingStar from "components/organisms/landing/FlashingStar";
import ServiceIntroSection from "components/organisms/landing/ServiceIntroSection";
import GuestUserLayout from "components/templates/GuestUserLayout";

import { useUser } from "hooks/queries/useUser";

import { useDevice } from "utils/device";

import AOS from "aos";
import "aos/dist/aos.css";

const ContentsWrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 0;
    padding: 0;
    width: 100%;
    min-height: calc(100vh - 48px);

    ${(props) =>
        props.device.isTabletOrPC &&
        css`
            min-height: calc(100vh - 56px);
        `};
`;

const TopSection = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 64px 0 0 0;
    width: 100%;
    height: auto;

    ${(props) =>
        props.device.isTabletOrPC &&
        css`
            padding: 24px 0 0 0;
        `};
`;

const WidthLimit = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin: 0 auto;
    padding: 0 40px;
    width: 100%;
    max-width: 1080px;

    ${(props) =>
        props.device.isTabletOrPC &&
        css`
            flex-direction: row;
            justify-content: space-between;
        `};
`;

const LogoCopyWrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 0;
    width: 100%;

    ${(props) =>
        props.device.isTabletOrPC &&
        css`
            align-items: flex-start;
            margin: 0 20px 0 0;
            width: calc(100% - 340px);
        `};

    ${(props) =>
        props.device.isPC &&
        css`
            width: calc(100% - 460px);
        `};
`;

const LandingLogoWrap = styled.div`
    height: 32px;
    transform: scale(0.8);

    > img {
        height: 100%;
    }

    ${(props) =>
        props.device.isTabletOrPC &&
        css`
            hegiht: 40px;
            transform: scale(1);
        `};
`;

const HeroMessage = styled.h1`
    margin: 24px 0 0 0;
    font-size: 32px;
    font-family: "LINESeedKR";
    color: var(--color-NormalBlack);
    text-align: center;

    ${(props) =>
        props.device.isTabletOrPC &&
        css`
            font-size: 40px;
            text-align: left;
            margin: 40px 0 0 0;
        `};
`;

const HeroSubMessage = styled(Text).attrs((props) => ({
    variant: "body-lg-400",
    margin: props.device?.isTabletOrPC ? "24px 0 0 0" : "16px 0 0 0",
    textAlign: props.device?.isTabletOrPC ? "left" : "center",
    width: "fit-content",
    height: "fit-content",
    color: "var(--color-SubBlack)",
}))``;

const KeyColorText = styled.span`
    color: var(--color-Key);
`;

const LandingImageWrap = styled.div`
    position: relative;
    margin: 24px 0 0 0;
    padding: 24px 0;
    width: 240px;

    > img {
        width: 100%;
        opacity: 0.9;
    }

    ${(props) =>
        props.device.isTabletOrPC &&
        css`
            display: block;
            margin: 0;
            width: 360px;
        `};

    ${(props) =>
        props.device.isPC &&
        css`
            width: 440px;
        `};
`;

const AnimationWrap = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: transparent;
`;

function LandingPage(props) {
    const device = useDevice();

    const navigate = useNavigate();
    const isTokenAuthed = !!localStorage.getItem("token");

    // 로그인일 때만 요청되도록
    const userQuery = useUser({ enabled: isTokenAuthed });

    useEffect(() => {
        // 로그인 상태에서는 참고문헌 관리로 이동
        if (!isTokenAuthed) return;
        if (userQuery.data?.is_authenticated !== true) return;

        const privateSpaceUuid = userQuery.data?.private_space?.uuid;
        if (privateSpaceUuid) {
            navigate(`/${privateSpaceUuid}/reference`);
        }
    }, [userQuery.data, isTokenAuthed, navigate]);

    useEffect(() => {
        AOS.init({
            offset: 100,
            duration: 800,
            easing: "ease-in-out",
            once: true,
        });
    }, []);

    return (
        <GuestUserLayout>
            <HtmlHead title={"PREP"} />
            <ContentsWrap device={device}>
                <TopSection device={device}>
                    <WidthLimit device={device}>
                        <LogoCopyWrap data-aos="fade-up" device={device}>
                            <LandingLogoWrap device={device}>
                                <Image name={"landingLogo"} />
                            </LandingLogoWrap>
                            <HeroMessage device={device}>
                                연구자를 위한 {!device.isTabletOrPC && <br />}최고의 선택{" "}
                                <KeyColorText>프렙</KeyColorText>
                            </HeroMessage>

                            <HeroSubMessage device={device}>
                                논문 검색과 참고문헌 관리부터
                                <br />
                                AI와 함께하는 PDF 리뷰 및 동향 분석 연구 노트까지,
                                <br />
                                개인, 그룹 단위의 연구 공간에서 당신의 연구 속도를 높여보세요.
                            </HeroSubMessage>
                        </LogoCopyWrap>
                        <LandingImageWrap data-aos="fade-up" data-aos-delay="50" device={device}>
                            <img src={LandingHeroImage} alt={"배경이미지"} />
                            <AnimationWrap>
                                <FlashingStar />
                            </AnimationWrap>
                        </LandingImageWrap>
                    </WidthLimit>
                </TopSection>

                <ServiceIntroSection />
            </ContentsWrap>
        </GuestUserLayout>
    );
}

export default LandingPage;
